import { HttpMethod, httpClient } from './http-client';
import { LocationNodeData } from '../@types/live-search.type';
import {
  PartType,
  SoftwareFirmwareHardwareData,
  SoftwareFirmwareHardwareOptions,
  WarrantyData,
  ChildPartTypeData,
  SalePricingObject,
  SalesRepairSupplierData,
  PendingTransfersList,
  PartTypeLiveSearchItem
} from '../@types/partType.type';
import { SerialPopupObject } from '../@types/shipment.type';
import { RequestDataType } from '../@types/tlc.type';
import { ConfigurationHistoryListResponse } from '../queries/part-type-query';

export const getPartTypeById = (id: number): Promise<PartType> => {
  return httpClient<PartType>(`/partTypes/${id}`, HttpMethod.Get);
};

export const getPartTypeByIdForService = (id: number): Promise<PartType> => {
  return httpClient<PartType>(`/partTypes/${id}/forService`, HttpMethod.Get);
};

export const getAllConfigurationHistory = (partTypeId: number): Promise<ConfigurationHistoryListResponse[]> => {
  return httpClient<ConfigurationHistoryListResponse[]>(`/configurationHistories/getByPartType/${partTypeId}`, HttpMethod.Get);
};

export const createPartType = (partType: Partial<PartType>): Promise<PartType> => {
  return httpClient<PartType>('/partTypes', HttpMethod.Post, partType);
};

export const updatePartType = (id: number, partType: Partial<PartType>): Promise<PartType> => {
  return httpClient<PartType>(`/partTypes/${id}`, HttpMethod.Put, partType);
};

export const createWarranty = (warranty: WarrantyData): Promise<WarrantyData & { partTypeId: number }> => {
  return httpClient<WarrantyData & { partTypeId: number }>('/warranties', HttpMethod.Post, warranty);
};

export const updateWarranty = (id: number, warranty: WarrantyData): Promise<WarrantyData & { partTypeId: number }> => {
  return httpClient<WarrantyData & { partTypeId: number }>(`/warranties/${id}`, HttpMethod.Put, warranty);
};

export const createSupplier = (supplier: SalesRepairSupplierData): Promise<SalesRepairSupplierData & { partTypeId: number }> => {
  return httpClient<SalesRepairSupplierData & { partTypeId: number }>('/supplierPricings', HttpMethod.Post, supplier);
};

export const updateSupplier = (id: number, supplier: SalesRepairSupplierData): Promise<SalesRepairSupplierData & { partTypeId: number }> => {
  return httpClient<SalesRepairSupplierData & { partTypeId: number }>(`/supplierPricings/${id}`, HttpMethod.Put, supplier);
};

export const createSFHware = (sfhware: SoftwareFirmwareHardwareData): Promise<SoftwareFirmwareHardwareData & { partTypeId: number }> => {
  return httpClient<SoftwareFirmwareHardwareData & { partTypeId: number }>('/sfhwares', HttpMethod.Post, sfhware);
};

export const updateSFHware = (id: number, sfhware: SoftwareFirmwareHardwareData): Promise<SoftwareFirmwareHardwareData & { partTypeId: number }> => {
  return httpClient<SoftwareFirmwareHardwareData & { partTypeId: number }>(`/sfhwares/${id}`, HttpMethod.Put, sfhware);
};

export const createSalePricing = (partTypeId: number, salePricing: SalePricingObject): Promise<SalePricingObject & { partTypeId: number }> => {
  return httpClient<SalePricingObject & { partTypeId: number }>(`/partTypes/salePricings/${partTypeId}`, HttpMethod.Post, salePricing);
};

export const updateSalePricing = (id: number, salePricing: SalePricingObject): Promise<SalePricingObject & { partTypeId: number }> => {
  return httpClient<SalePricingObject & { partTypeId: number }>(`/partTypes/salePricings/${id}`, HttpMethod.Put, salePricing);
};

export const createRepairPricing = (partTypeId: number, repairPricing: SalesRepairSupplierData): Promise<SalesRepairSupplierData & { partTypeId: number }> => {
  return httpClient<SalesRepairSupplierData & { partTypeId: number }>(`/partTypes/repairPricings/${partTypeId}`, HttpMethod.Post, repairPricing);
};

export const updateRepairPricing = (id: number, repairPricing: SalesRepairSupplierData): Promise<SalesRepairSupplierData & { partTypeId: number }> => {
  return httpClient<SalesRepairSupplierData & { partTypeId: number }>(`/partTypes/repairPricings/${id}`, HttpMethod.Put, repairPricing);
};

export const createChildPartType = (partTypeId: number, childPartType: ChildPartTypeData): Promise<ChildPartTypeData> => {
  return httpClient<ChildPartTypeData>(`/partTypes/childrens/${partTypeId}`, HttpMethod.Post, childPartType);
};

export const updateChildPartType = (partTypeId: number, childPartType: ChildPartTypeData): Promise<ChildPartTypeData> => {
  return httpClient<ChildPartTypeData>(`/partTypes/childrens/${partTypeId}`, HttpMethod.Put, childPartType);
};

export const deleteChildPartType = (partTypeId: number, childPartTypeId: number): Promise<void> => {
  return httpClient<void>(`/partTypes/${partTypeId}/childrens/${childPartTypeId}`, HttpMethod.Delete);
};

export const getPartTypesByClientContract = (clientId: number, contractId?: number): Promise<PartTypeLiveSearchItem[]> => {
  let url = `/partTypes/client/${clientId}`;
  if (contractId) {
    url += `?contractId=${contractId}`;
  }
  return httpClient<PartTypeLiveSearchItem[]>(url, HttpMethod.Get);
};

export const getPartTypeSFHwaresDetails = (partTypeId: number): Promise<SoftwareFirmwareHardwareOptions> => {
  return httpClient<SoftwareFirmwareHardwareOptions>(`/partTypes/${partTypeId}/sfhwares`, HttpMethod.Get);
};

export const getPartTypeManifestById = (id: number): Promise<SerialPopupObject> => {
  return httpClient<SerialPopupObject>(`/partTypes/manifest/${id}`, HttpMethod.Get);
};

// This API can be used to retrieve shipment manifest data for a part type by passing the shipment status codes
export const getPartTypeShipmentDataByPartTypeIdAndShipmentStatus = (partTypeId: number, isSerialized: boolean, shipmentStatusCodes: string): Promise<PendingTransfersList[]> => {
  return httpClient<PendingTransfersList[]>(`/partTypes/${partTypeId}/shipmentData?isSerialized=${isSerialized}&shipmentStatusCodes=${shipmentStatusCodes}`, HttpMethod.Get);
};

export const getSearchTLCPartTypes = (data: string): Promise<PartType[]> => {
  return httpClient<PartType[]>(`/partTypeSearchTLC?searchData=${data}`, HttpMethod.Get);
};

export const getSearchTLCPartTypeLocation = (data: RequestDataType): Promise<LocationNodeData[]> => {
  return httpClient<LocationNodeData[]>(`/partTypeLocationSearch?isIncludeChild=${data.isIncludeChild}&searchData=${data.searchData}`, HttpMethod.Get);
};
